/*
 * AdminApp — the admin portal (separate surface). Reviews creator-mode
 * requests: approve / reject pending ones, disable or re-approve existing
 * creators. Server-side admin check on every call — this UI is only reachable
 * when /admin/me says is_admin, and harmless if someone forces it open.
 */
import React, { useCallback, useEffect, useState } from 'react';
import '../../styles/creator.css';
import APIService from '../../services/APIService';
import { IconShield } from '../nano/icons';

const STATUS_META = {
  pending: ['Pending', 'var(--n-warn-ink)', 'var(--n-warn-soft)'],
  approved: ['Approved', 'var(--n-ok-ink)', 'var(--n-ok-soft)'],
  rejected: ['Rejected', 'var(--n-err-ink)', 'var(--n-err-soft)'],
  disabled: ['Disabled', 'var(--n-text-3)', 'var(--n-surface-3)'],
};

export default function AdminApp({ onExit }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(() => {
    APIService.adminCreators()
      .then((d) => setRows(d.creators || []))
      .catch((e) => { setError(e.message); setRows([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (row, status) => {
    setBusyId(row.creator_id);
    try {
      await APIService.adminSetStatus(row.creator_id, status);
      load();
    } catch (e) { setError(e.message); } finally { setBusyId(''); }
  };

  const pending = (rows || []).filter((r) => r.status === 'pending');
  const rest = (rows || []).filter((r) => r.status !== 'pending');

  const Row = ({ r }) => {
    const [label, color, bg] = STATUS_META[r.status] || STATUS_META.pending;
    return (
      <div className="ca-row">
        <div className="ca-row-main">
          <span className="ca-row-t">{r.display_name || r.creator_id}</span>
          <span className="ca-row-m">{r.email || 'no email'} · requested {r.requested_at?.slice(0, 10)}</span>
        </div>
        <span className="pf-chip" style={{ color, background: bg }}>{label}</span>
        {r.status === 'pending' && (
          <>
            <button className="ca-primary" type="button" disabled={busyId === r.creator_id}
              onClick={() => setStatus(r, 'approved')}>Approve</button>
            <button className="ca-ghost" type="button" disabled={busyId === r.creator_id}
              onClick={() => setStatus(r, 'rejected')}>Reject</button>
          </>
        )}
        {r.status === 'approved' && (
          <button className="ca-ghost" type="button" disabled={busyId === r.creator_id}
            onClick={() => setStatus(r, 'disabled')}>Disable</button>
        )}
        {(r.status === 'rejected' || r.status === 'disabled') && (
          <button className="ca-ghost" type="button" disabled={busyId === r.creator_id}
            onClick={() => setStatus(r, 'approved')}>Approve</button>
        )}
      </div>
    );
  };

  return (
    <div className="nano-root ca-root" style={{ '--mx': '#94a3b8' }}>
      <div className="nano-glow" aria-hidden="true" />
      <aside className="ca-side">
        <div className="ca-brand">✦ DataVerse.ai <span>Admin</span></div>
        <div className="ca-nav on"><span className="ca-nav-ic"><IconShield size={16} /></span>Creator requests</div>
        <div className="ca-side-spacer" />
        <button type="button" className="ca-exit" onClick={onExit}>← Back to app</button>
      </aside>
      <main className="ca-main">
        <div className="ca-panel">
          <div className="ca-panel-h">Creator access</div>
          <p className="ca-hint">Users who request creator mode appear here. Approval unlocks their studio; disable revokes it (their content stays, unpublished from storefronts).</p>
          {error && <div className="ca-error">{error}</div>}
          {rows === null ? <div className="ca-hint">Loading…</div> : (
            <>
              <div className="ca-panel-h" style={{ fontSize: 14, marginTop: 10 }}>Pending ({pending.length})</div>
              {pending.length === 0
                ? <div className="ca-hint">No pending requests.</div>
                : <div className="ca-list">{pending.map((r) => <Row key={r.creator_id} r={r} />)}</div>}
              <div className="ca-panel-h" style={{ fontSize: 14, marginTop: 22 }}>All creators ({rest.length})</div>
              {rest.length === 0
                ? <div className="ca-hint">None yet.</div>
                : <div className="ca-list">{rest.map((r) => <Row key={r.creator_id} r={r} />)}</div>}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
